import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { useState, useRef } from "react";
import JSZip from "jszip";

// Styles
import tableStyles from "./../../../theme/table.module.css";

// Interfaces
import { Campaign } from "../../../interfaces";

// Components
import SkeletonText from "../../SkeletonText";

import FileInput from "../../common/file-input";

// API requests
import CrmAPI from "../../../api/crm";

const List = ({ isMobile, loading, list, refreshList }: ILayoutProps) => {
  console.log({ isMobile, loading, list });
  const [isWorking, setIsWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);

  /*
  const sendCampaignRequest = async (campaignId: string) => {
    setIsWorking(true);

    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }

    const { data, err } = await CrmAPI.sendCampaign(campaignId);
    setIsWorking(false);

    if (err) {
      // Handle error (e.g., show a notification)
      alert("could not send campaign");
      return;
    }

    if (data) {
      // Handle success (e.g., show a notification)
      alert("Campaigns sent successfully to all contacts");
      return;
    }

    console.log(data, err);
  };
  */

  const deleteCampaign = async (campaignId: string) => {
    setIsWorking(true);
    const { data, err } = await CrmAPI.deleteCampaign(campaignId);
    setIsWorking(false);

    if (err) {
      alert("could not delete campaign");
      return;
    }

    if (data) {
      alert("Campaign deleted: " + data.campaignName);
      refreshList();
      return;
    }
  };

  const escapeCSVValue = (value: any): string => {
    const stringValue = String(value ?? ""); // Maneja null/undefined
    const escaped = stringValue.replace(/"/g, '""'); // Escapa comillas dobles
    return `"${escaped}"`; // Encierra todo entre comillas dobles
  };

  const download = (blob: Blob, filename = "data.csv") => {
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadCSVTemplate = async (campaignId: string) => {
    setIsWorking(true);
    const { data, err } = await CrmAPI.downloadCSVTemplate(campaignId);
    setIsWorking(false);
    if (data) {
      const { rows } = data;
      const zip = new JSZip();
      if (rows?.length) {
        const escapedRows = rows.map((row: any[]) =>
          row.map(escapeCSVValue).join(","),
        );
        zip.file("leads.csv", escapedRows.join("\n"));
      }

      const blob = await zip.generateAsync({ type: "blob" });
      download(blob, "sample-csv.zip");
    }

    if (err) {
      alert("could not download CSV template");
      return;
    }

    if (data) {
      alert("did something");
    }
  };

  const onSubmit = (campaignId: string) => {
    if (!file) {
      setErrorMessage("Please select a file first.");
      return;
    }

    setIsWorking(true);
    setErrorMessage(null);

    const formData = new FormData();
    formData.append("file", file);

    CrmAPI.sendCampaign(campaignId, formData)
      .then(({ data, err }) => {
        setIsWorking(false);

        if (err) {
          setErrorMessage("There was an error sending the campaign.");
          return;
        }

        if (data) {
          alert("Campaign sent successfully to all contacts.");
          setFile(null);
          if (hiddenFileInput.current) {
            hiddenFileInput.current.value = "";
          }
          return;
        }
      })
      .catch(() => {
        setIsWorking(false);
        setErrorMessage("There was an error sending the campaign.");
      });
  };

  const onChangeFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
    }
  };

  return isMobile ? (
    loading ? (
      <div>
        <SkeletonText rows={6} />
      </div>
    ) : (
      <div>
        <div className="fw-semibold mb-3 mt-3">
          <FormattedMessage id="crm.campaignsListTitle" />
        </div>
      </div>
    )
  ) : loading ? (
    <div>
      <SkeletonText rows={12} />{" "}
    </div>
  ) : (
    <div className="row">
      <div className="col-md-12">
        <div className={tableStyles.tableContainer}>
          <table className="table mb-0">
            <thead className={tableStyles.theadFixed}>
              <tr className={tableStyles.tableHeader}>
                <th className={tableStyles.tableTitle}>
                  <FormattedMessage id="campaigns.CreationDate" />
                </th>
                <th className={tableStyles.tableTitle}>
                  <FormattedMessage id="campaigns.Name" />
                </th>
                <th className={tableStyles.tableTitle}>
                  <FormattedMessage id="campaigns.templateName" />
                </th>
                <th className={tableStyles.tableTitle}></th>
              </tr>
            </thead>
            <tbody className={tableStyles.tableBody}>
              {list && list.length === 0 && (
                <tr className="fs-3">
                  <td className="text-center" colSpan={4}>
                    <FormattedMessage id="common.noResults" />
                  </td>
                </tr>
              )}
              {list &&
                list.map((o) => (
                  <tr key={o.id}>
                    <td
                      className={`${tableStyles.tdFullWidth} ${tableStyles.commonTd}`}
                    >
                      {o.timestamp}
                    </td>
                    <td className={tableStyles.commonTd}>
                      <Link to={`/crm/campaigns/${o.id}`}>{o.name}</Link>
                    </td>
                    <td className={tableStyles.commonTd}>
                      {o.template} - {o.language}
                    </td>
                    <td className={tableStyles.commonTd}>
                      <FileInput
                        ref={hiddenFileInput}
                        accept=".csv"
                        onChange={(e) => onChangeFileInput(e)}
                        style={{ display: "none" }}
                      />

                      <button
                        disabled={isWorking}
                        className="btn btn-visito-primary"
                        style={{ marginRight: "14px" }}
                        onClick={() => hiddenFileInput.current?.click()}
                      >
                        <FormattedMessage id="Upload CSV" />
                      </button>

                      {file && (
                        <button
                          disabled={isWorking}
                          className="btn btn-visito-primary"
                          style={{ marginRight: "14px" }}
                          onClick={() => onSubmit(o.id)}
                        >
                          {isWorking ? "..." : `Send ${file?.name}`}
                        </button>
                      )}
                      {/*
                      <button
                        disabled={isWorking}
                        className="btn btn-visito-primary"
                        style={{ marginRight: "14px" }}
                        onClick={() => deleteCampaign(o.id)}
                      >
                        {isWorking ? "..." : <FormattedMessage id="Delete" />}
                      </button>
                      */}
                      <button
                        disabled={isWorking}
                        className="btn btn-visito-primary"
                        onClick={() => downloadCSVTemplate(o.id)}
                      >
                        {isWorking ? (
                          "..."
                        ) : (
                          <FormattedMessage id="Download CSV format" />
                        )}
                      </button>
                      {errorMessage}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

interface ILayoutProps {
  isMobile: boolean;
  loading: boolean;
  list: Campaign[] | undefined;
  refreshList: () => void;
}

export default List;
